import { update } from '@/services/Downloads';
import { Features } from '@/services/Features';
import * as yup from 'yup';

export interface InsuredsPolicyInformation {
	policyNumber: string;
	nameOfInsured: string;
	reportingForAAN: 'Individual' | 'Group' | 'Both' | '';
}

export interface PrimaryContactForThisEventorClaim {
	sameAs?: boolean;
	sameAsUser?: string;
	relationToInsured: string;
	otherRelationToInsured: string;
	firstName: string;
	lastName: string;

	// Contact Information
	phonePreference: string;
	phoneNumber: string;
	emailAddress: string;

	specialInstructions?: string;
}

export type PhoneNumberType = 'Home' | 'Mobile' | 'Work / Office';

export function phoneNumberTypeSchema(): yup.MixedSchema<PhoneNumberType> {
	return yup.mixed<PhoneNumberType>().oneOf(['Home', 'Mobile', 'Work / Office']);
}

export type YesNo = 'No' | 'Yes';

export function yesNoSchema(): yup.MixedSchema<YesNo> {
	return yup.mixed<YesNo>().oneOf(['No', 'Yes']);
}

export type YesNoUnknown = 'No' | 'Unknown' | 'Yes';

export function yesNoUnknownSchema(): yup.MixedSchema<YesNoUnknown> {
	return yup.mixed<YesNoUnknown>().oneOf(['No', 'Unknown', 'Yes']);
}

/**
 * A regular expression that matches a date ***or the empty string***. This is so the regex can
 * be used solely for validation of nonempty inputs while letting the presence or absence of
 * `yup.string().required()` determine whether an empty string is acceptable.
 */
const dateRegex: RegExp = /^((0[1-9]|1[012])[- /.](0[1-9]|[12][0-9]|3[01])[- /.](19|20)\d\d)?$/;

export interface InformationAboutThePatientOrClaimantInvolved {
	patientType: string;
	other?: string;
	firstName: string;
	middleInitial?: string;
	lastName: string;
	suffix?: string;
	medicareBeneficaryHicNumber?: string;
	dateOfBirth: string;
	dateOfDeath?: string;
	martialStatus?: string;
	noOfDependents?: string;
	occupation?: string;
	gender?: string;
	// Primary Practice / Business Address
	buildingOrganization?: string;
	streetAddress: string;
	suiteAptUnit?: string;
	city: string;
	state: string;
	zipcode: string;

	// Contact Information
	phonePreference: PhoneNumberType | undefined;
	phoneNumber: string;
	emailAddress: string;
	isThePatientClaimantAMedicareBeneficiary: YesNoUnknown | undefined;
	residentialAddressSameAsPatient: boolean;
	contactInformationSameAsPatient: boolean;

	// Patient or Claimant
	isThePatientSameAsClaimant: YesNo | undefined;

	claimant?: {
		firstName: string;
		middleInitial?: string;
		lastName: string;
		suffix?: string;
		relationToSpouse?: string;
		martialStatus?: string;
		other?: string;
		// Primary Practice / Business Address
		buildingOrganization?: string;
		streetAddress: string;
		suiteAptUnit?: string;
		city: string;
		state: string;
		zipcode: string;

		// Contact Information
		phonePreference: string;
		phoneNumber: string;
		emailAddress: string;
	};
}

export interface InformationAboutTheClaimIncident {
	dateOfIncident: string;
	dateNotifiedOfIncident: string;

	lossLocationName?: string;
	sameAddressAs: boolean;
	sameAddress: string;

	// Primary Practice / Business Address
	buildingOrganization?: string;
	streetAddress: string;
	suiteAptUnit?: string;
	city: string;
	state: string;
	zipcode: string;

	reportType: string;
	reportTypeOther: string;
	dateServed: string;

	actionRequest: string;
	descriptionOfIncident: string;

	otherInsuranceCarrierName?: string;
	anyOtherApplicableInsuranceCoverage?: string;
}

export interface SupportDocuments {
	files: File[] | undefined;
	fileLog: any;
	uploads: any;
}

export interface ClaimantPlantiffAttorneyInformation {
	firstName: string;
	lastName: string;
	middleInitial?: string;
	suffix?: string;
	designation?: string;
	lawFirmName: string;

	// Primary Practice / Business Address
	buildingOrganization?: string;
	streetAddress?: string;
	suiteAptUnit?: string;
	city?: string;
	state?: string;
	zipcode?: string;

	// Contact Information
	phonePreference: string;
	phoneNumber: string;
	emailAddress: string;
}

export interface NoticiesSignaturesSubmissionOfReport {
	readAndAcceptNoticies: boolean;

	nameOfPersonSubmittingReport: string;
	relationToInsured: string;
	otherRelationToInsured: string;
	authorizedToSubmt: boolean;
	guiltyOfCrime: boolean;
}

export interface Provider {
	firstName: string;
	lastName: string;
	middleInitial?: string;
	suffix?: string;
	designation?: string;
	isThisProviderNamedOnThePolicy: boolean | '';
	nationalProviderIdentifier?: string;
	gender?: string;
	dateOfBirth?: string;

	// Primary Practice / Business Address
	buildingOrganization?: string;
	streetAddress: string;
	suiteAptUnit?: string;
	city: string;
	state: string;
	zipcode: string;

	// Contact Information
	phonePreference: string;
	phoneNumber: string;
	emailAddress: string;
	sameAddressAs: boolean;
	sameAddress: string;
	sameContactAs: boolean;
	sameContact: string;
}

export interface Group {
	groupName: string;

	// Primary Practice / Business Address
	buildingOrganization?: string;
	streetAddress: string;
	suiteAptUnit?: string;
	city: string;
	state: string;
	zipcode: string;

	// Contact Information
	phonePreference: string;
	phoneNumber: string;
	emailAddress: string;
}

export interface Fields {
	isUserLoggedIn: boolean;
	brand: string;
	insuredsPolicyInformation: InsuredsPolicyInformation;
	involvedInsuredsHealthCareProviders: {
		group?: Group;
		providers?: Provider[];
	};
	primaryContactForThisEventorClaim: PrimaryContactForThisEventorClaim;
	informationAboutThePatientOrClaimantInvolved: InformationAboutThePatientOrClaimantInvolved;
	informationAboutTheClaimIncident: InformationAboutTheClaimIncident;
	supportDocuments: SupportDocuments;
	claimantPlantiffAttorneyInformation?: ClaimantPlantiffAttorneyInformation;
	noticiesSignaturesSubmissionOfReport: NoticiesSignaturesSubmissionOfReport;
}

export interface VuexState {
	fields: Fields;
	emptyProvider: Provider;
}

/**
 * Form Validation with Yup
 */
export const validationSchema = yup.object({
	isUserLoggedIn: yup.boolean(),
	brand: yup.mixed().when('$values', {
			is: values => !values.isUserLoggedIn,
			then: yup
				.string()
				.required("Select your Brand")
				.label('Brand'),
		}),
	insuredsPolicyInformation: yup.object({
		policyNumber: yup
			.string()
			.required('Select your policy number')

			.label('Policy Number'),
		nameOfInsured: yup.string(),

		reportingForAAN: yup
			.string()
			.required()
			.label('Reporting For A/AN'),
	}),
	involvedInsuredsHealthCareProviders: yup.object({
		group: yup.object().when('$values', {
			is: value => {
				return (
					value.insuredsPolicyInformation.reportingForAAN === 'Group' ||
					value.insuredsPolicyInformation.reportingForAAN === 'Both'
				);
			},
			then: yup.object({
				groupName: yup
					.string()
					.required()
					.label('Entity Name'),
				buildingOrganization: yup.string().label('Building / Organization'),
				streetAddress: yup
					.string()
					.required()
					.label('Street Address'),
				suiteAptUnit: yup.string().label('Suite / Apt / Unit'),
				city: yup
					.string()
					.required()
					.label('City'),
				state: yup
					.string()
					.required()
					.label('State'),
				zipcode: yup
					.string()
					.required()
					.label('Zip Code'),
				phonePreference: yup
					.string()
					.required()
					.label('Phone Preference'),
				phoneNumber: yup
					.string()
					.required()
					.label('Phone Number'),
				emailAddress: yup
					.string()
					.required()
					.label('Email'),
			}),
		}),
		providers: yup.mixed().when('$values', {
			is: value => {
				return (
					value.insuredsPolicyInformation.reportingForAAN === 'Individual' ||
					value.insuredsPolicyInformation.reportingForAAN === 'Both'
				);
			},
			then: yup.array().of(
				yup.object({
					firstName: yup
						.string()
						.required("Provider's First Name is required.")
						.label('First Name'),
					lastName: yup
						.string()
						.required("Provider's Last Name is required.")
						.label('Last Name'),
					middleInitial: yup.string(),
					suffix: yup.string(),
					designation: yup.string(),
					nationalProviderIdentifier: yup.string(),
					gender: yup.string(),
					dateOfBirth: yup
						.string()
						.matches(dateRegex, 'Enter Date of Birth in MM/DD/YYYY')
						.label('Date of Birth'),
					buildingOrganization: yup.string().label('Building / Organization'),
					sameAddressAs: yup.boolean().label('Same Address As'),
					sameContactAs: yup.boolean().label('Same contact information As'),
					streetAddress: yup.mixed().when('sameAddressAs', {
						is: sameAddressAs => sameAddressAs === false,
						then: yup
							.string()
							.required()
							.label('Street Address'),
					}),
					suiteAptUnit: yup.mixed().when('sameAddressAs', {
						is: sameAddressAs => sameAddressAs === false,
						then: yup
							.string()

							.label('Suite / Apt / Unit'),
					}),
					city: yup.mixed().when('sameAddressAs', {
						is: sameAddressAs => sameAddressAs === false,
						then: yup
							.string()
							.required()
							.label('City'),
					}),
					state: yup.mixed().when('sameAddressAs', {
						is: sameAddressAs => sameAddressAs === false,
						then: yup
							.string()
							.required()
							.label('State'),
					}),
					zipcode: yup.mixed().when('sameAddressAs', {
						is: sameAddressAs => sameAddressAs === false,
						then: yup
							.string()
							.required()
							.label('Zip'),
					}),
					phonePreference: yup.mixed().when('sameContactAs', {
						is: sameContactAs => sameContactAs === false,
						then: yup
							.string()
							.required()
							.label('Phone Preference'),
					}),
					phoneNumber: yup.mixed().when('sameContactAs', {
						is: sameContactAs => sameContactAs === false,
						then: yup
							.string()
							.required()
							.label('Phone Number'),
					}),
					emailAddress: yup.mixed().when('sameContactAs', {
						is: sameContactAs => sameContactAs === false,
						then: yup
							.string()
							.required()
							.label('Email'),
					}),
				}),
			),
		}),
	}),
	primaryContactForThisEventorClaim: yup.object({
		sameAs: yup.boolean(),
		sameAsUser: yup.mixed().when('$values', {
			is: value => value.primaryContactForThisEventorClaim.sameAs === true,
			then: yup
				.string()
				.required()
				.label('Same As'),
		}),
		relationToInsured: yup
			.string()
			.required()
			.label('Relation to Insured'),
		otherRelationToInsured: yup.mixed().when('$values', {
			is: value => value.primaryContactForThisEventorClaim.relationToInsured === 'Other',
			then: yup
				.string()
				.required()
				.label('Other Relation To Insured'),
		}),

		firstName: yup.mixed().when('$values', {
			is: values => !values.primaryContactForThisEventorClaim.sameAs,
			then: yup
				.string()
				.required("Primary Contact's First Name is required.")

				.label('First Name'),
		}),
		lastName: yup.mixed().when('$values', {
			is: values => !values.primaryContactForThisEventorClaim.sameAs,
			then: yup
				.string()

				.required("Primary Contact's Last Name is required.")
				.label('Last Name'),
		}),
		phonePreference: yup.mixed().when('$values', {
			is: values => !values.primaryContactForThisEventorClaim.sameAs,
			then: yup
				.string()
				.required()
				.label('Phone Preference'),
		}),
		phoneNumber: yup.mixed().when('$values', {
			is: values => !values.primaryContactForThisEventorClaim.sameAs,
			then: yup
				.string()
				.required()
				.label('Phone Number'),
		}),
		emailAddress: yup.mixed().when('$values', {
			is: values => !values.primaryContactForThisEventorClaim.sameAs,
			then: yup
				.string()
				.required()
				.label('Email'),
		}),
		specialInstructions: yup.string(),
	}),
	informationAboutThePatientOrClaimantInvolved: yup.object<
		InformationAboutThePatientOrClaimantInvolved
	>({
		patientType: yup
			.string()
			.required()
			.label('Patient/Claimant type'),
		other: yup.mixed().when('$values', {
			is: value => value.informationAboutThePatientOrClaimantInvolved.patientType === 'Other',
			then: yup
				.string()
				.required()
				.label('Other'),
		}),
		firstName: yup
			.string()
			.required("Patient/Claimant's First Name is required.")
			.label('First Name'),
		middleInitial: yup.string().label('M.I.'),
		lastName: yup
			.string()
			.required()
			.label("Patient/Claimant's Last Name is required."),
		suffix: yup.string().label('Suffix'),
		medicareBeneficaryHicNumber: yup.string().label('Medicare Beneficiary HIC Number'),
		dateOfBirth: yup
			.string()
			.matches(dateRegex, 'Enter Date of Birth in MM/DD/YYYY')
			.label('Date of Birth'),
		dateOfDeath: yup
			.string()
			.matches(dateRegex, 'Enter Date of Death in MM/DD/YYYY')
			.label('Date of Death'),
		martialStatus: yup.string().label('Martial Status'),
		noOfDependents: yup.string().label('No of Dependents'),
		occupation: yup.string().label('Occupation'),
		buildingOrganization: yup.string().label('Building / Organization'),
		streetAddress: yup.string().label('Street Address'),
		suiteAptUnit: yup.string().label('Suite / Apt / Unit'),
		city: yup.string().label('City'),
		state: yup.string().label('State'),
		zipcode: yup.string().label('Zip Code'),
		isThePatientSameAsClaimant: yesNoSchema()
			.required()
			.label('Is The Patient Same as Claimant'),
		residentialAddressSameAsPatient: yup.boolean(),
		contactInformationSameAsPatient: yup.boolean(),
		claimant: yup.mixed().when('$values', {
			is: values =>
				values.informationAboutThePatientOrClaimantInvolved.isThePatientSameAsClaimant ===
				'No',
			then: yup.object({
				firstName: yup
					.string()
					.required()
					.label('First Name'),
				lastName: yup
					.string()
					.required()
					.label('Last Name'),
				middleInitial: yup.string(),
				suffix: yup.string(),
				relationToSpouse: yup
					.string()
					.required()
					.label('Relation to Patient'),
				other: yup.mixed().when('$values', {
					is: values =>
						values.informationAboutThePatientOrClaimantInvolved.claimant
							.relationToSpouse === 'Other',
					then: yup
						.string()
						.required()
						.label('Other Relation to Patient'),
				}),
				buildingOrganization: yup.string().label('Building / Organization'),
				streetAddress: yup.mixed().when('$values', {
					is: value =>
						!value.informationAboutThePatientOrClaimantInvolved
							.residentialAddressSameAsPatient,
					then: yup.string().label('Street Address'),
				}),

				suiteAptUnit: yup.string().label('Suite / Apt / Unit'),
				city: yup.mixed().when('$values', {
					is: value =>
						!value.informationAboutThePatientOrClaimantInvolved
							.residentialAddressSameAsPatient,
					then: yup.string().label('City'),
				}),
				state: yup.mixed().when('$values', {
					is: value =>
						!value.informationAboutThePatientOrClaimantInvolved
							.residentialAddressSameAsPatient,
					then: yup.string().label('State'),
				}),
				zipcode: yup.mixed().when('$values', {
					is: value =>
						!value.informationAboutThePatientOrClaimantInvolved
							.residentialAddressSameAsPatient,
					then: yup.string().label('Zip Code'),
				}),

				phonePreference: yup.mixed().when('$values', {
					is: value =>
						!value.informationAboutThePatientOrClaimantInvolved
							.contactInformationSameAsPatient,
					then: yup
						.string()
						.required()
						.label('Phone Preference'),
				}),

				phoneNumber: yup.mixed().when('$values', {
					is: value =>
						!value.informationAboutThePatientOrClaimantInvolved
							.contactInformationSameAsPatient,
					then: yup
						.string()
						.required()
						.label('Phone Number'),
				}),

				emailAddress: yup.mixed().when('$values', {
					is: value =>
						!value.informationAboutThePatientOrClaimantInvolved
							.contactInformationSameAsPatient,
					then: yup
						.string()
						.required()
						.label('Email'),
				}),
			}),
		}),
		isThePatientClaimantAMedicareBeneficiary: yesNoUnknownSchema()
			.required()
			.label('Is This Patient/Claimant a Medicare Beneficiary'),
		emailAddress: yup
			.string()
			.email()
			.required()
			.label('Email'),
		phoneNumber: yup.string().required(),
		phonePreference: phoneNumberTypeSchema()
			.required()
			.label('Phone Preference'),
	}),
	informationAboutTheClaimIncident: yup.object({
		dateOfIncident: yup
			.string()
			.required()
			.matches(dateRegex, 'Enter Date of Incident in MM/DD/YYYY')
			.label('Date of Incident'),
		dateNotifiedOfIncident: yup
			.string()
			.required()
			.matches(dateRegex, 'Enter Date Notified of Incident in MM/DD/YYYY')
			.label('Date Notified of Incident '),
		lossLocationName: yup
			.string()
			.required('Incident Location is required.')
			.label('Loss Location Name'),
		reportType: yup
			.string()
			.required()
			.label('Report Type'),
		reportTypeOther: yup.mixed().when('$values', {
			is: values => values.informationAboutTheClaimIncident.reportType === 'Others',
			then: yup
				.string()
				.required()
				.label('Other Report Type'),
		}),
		actionRequest: yup
			.string()
			.required()
			.label('Action Request'),
		descriptionOfIncident: yup
			.string()
			.required()
			.label('Description of Incident'),
		alreadyReportedToPriorCarrier: yup.string().label('Already Reported to a Prior Carrier'),
		anyOtherApplicableInsuranceCoverage: yup
			.string()
			.label('Any Other Applicable Insurance Coverage'),
		sameAddressAs: yup.boolean().label('Same Address As'),
		sameAddress: yup.mixed().when('$values', {
			is: value => value.informationAboutTheClaimIncident.sameAddressAs === true,
			then: yup
				.string()
				.required()
				.label('Same Address As'),
		}),
		buildingOrganization: yup.string().label('Building / Organization'),
		streetAddress: yup.mixed().when('$values', {
			is: values => values.informationAboutTheClaimIncident.sameAddressAs === false,
			then: yup
				.string()
				.required()
				.label('Street Address'),
		}),
		suiteAptUnit: yup.string().label('Suite / Apt / Unit'),
		city: yup.mixed().when('$values', {
			is: values => values.informationAboutTheClaimIncident.sameAddressAs === false,
			then: yup
				.string()
				.required()
				.label('City'),
		}),
		state: yup.mixed().when('$values', {
			is: values => values.informationAboutTheClaimIncident.sameAddressAs === false,
			then: yup
				.string()
				.required()
				.label('State'),
		}),

		zipcode: yup.mixed().when('$values', {
			is: values => values.informationAboutTheClaimIncident.sameAddressAs === false,
			then: yup
				.string()
				.required()
				.label('Zip Code'),
		}),
	}),
	claimantPlantiffAttorneyInformation: yup.object<ClaimantPlantiffAttorneyInformation>({
		firstName: yup.mixed().when('$values', {
			is: values =>
				values.informationAboutTheClaimIncident.reportType === 'Lawsuit' ||
				values.informationAboutTheClaimIncident.reportType ===
					'Deposition or Meeting Request',
			then: yup
				.string()
				.required()
				.label('First Name'),
			otherwise: yup.string().label('First Name'),
		}),

		lastName: yup.mixed().when('$values', {
			is: values =>
				values.informationAboutTheClaimIncident.reportType === 'Lawsuit' ||
				values.informationAboutTheClaimIncident.reportType ===
					'Deposition or Meeting Request',
			then: yup
				.string()
				.required()
				.label('Last Name'),
			otherwise: yup.string().label('Last Name'),
		}),
		suffix: yup.string().label('Suffix'),
		designation: yup.string().label('Designation'),
		lawFirmName: yup.mixed().when('$values', {
			is: values =>
				values.informationAboutTheClaimIncident.reportType === 'Lawsuit' ||
				values.informationAboutTheClaimIncident.reportType ===
					'Deposition or Meeting Request',
			then: yup
				.string()
				.required()
				.label('Law Firm Name'),
			otherwise: yup.string().label('Law Firm Name'),
		}),
		buildingOrganization: yup.string().label('Building / Organization'),
		streetAddress: yup.mixed().when('$values', {
			is: values =>
				Features.isClaimFormClaimantPlantiffAttorneyAddressEnabled &&
				(values.informationAboutTheClaimIncident.reportType === 'Lawsuit' ||
					values.informationAboutTheClaimIncident.reportType ===
						'Deposition or Meeting Request'),
			then: yup
				.string()
				.required()
				.label('Street Address'),
			otherwise: yup.string().label('Street Address'),
		}),
		suiteAptUnit: yup.string().label('Suite / Apt / Unit'),
		city: yup.mixed().when('$values', {
			is: values =>
				Features.isClaimFormClaimantPlantiffAttorneyAddressEnabled &&
				(values.informationAboutTheClaimIncident.reportType === 'Lawsuit' ||
					values.informationAboutTheClaimIncident.reportType ===
						'Deposition or Meeting Request'),
			then: yup
				.string()
				.required()
				.label('City'),
			otherwise: yup.string().label('City'),
		}),
		state: yup.mixed().when('$values', {
			is: values =>
				Features.isClaimFormClaimantPlantiffAttorneyAddressEnabled &&
				(values.informationAboutTheClaimIncident.reportType === 'Lawsuit' ||
					values.informationAboutTheClaimIncident.reportType ===
						'Deposition or Meeting Request'),
			then: yup
				.string()
				.required()
				.label('State'),
			otherwise: yup.string().label('State'),
		}),
		zipcode: yup.mixed().when('$values', {
			is: values =>
				Features.isClaimFormClaimantPlantiffAttorneyAddressEnabled &&
				(values.informationAboutTheClaimIncident.reportType === 'Lawsuit' ||
					values.informationAboutTheClaimIncident.reportType ===
						'Deposition or Meeting Request'),
			then: yup
				.string()
				.required()
				.label('Zip Code'),
			otherwise: yup.string().label('Zip Code'),
		}),
		phonePreference: yup.string().label('Phone Preference'),
		phoneNumber: yup.string().label('Phone Number'),
		emailAddress: yup.string().label('Email'),
	}),
	noticiesSignaturesSubmissionOfReport: yup.object({
		readAndAcceptNoticies: yup.boolean().required(),
		nameOfPersonSubmittingReport: yup
			.string()
			.required()
			.label('Name of person submitting report'),
		relationToInsured: yup
			.string()
			.required()
			.label('Relation to Insured'),
		authorizedToSubmt: yup
			.boolean()
			.required()
			.oneOf([true], 'Must tick checkbox on submission agreement.'),
		otherRelationToInsured: yup.mixed().when('$values', {
			is: values => values.noticiesSignaturesSubmissionOfReport.relationToInsured === 'Other',
			then: yup
				.string()
				.required()
				.label('Other Relation to Insured'),
			otherwise: yup.string(),
		}),
		guiltyOfCrime: yup
			.boolean()
			.required()
			.oneOf([true], 'Must tick checkbox on notice agreement.'),
	}),
});
